/**
 * Sim world — Stage 1c integration.
 *
 * Implements the `Sim` interface from types.ts. Owns the phase state machine
 * (title → playing → paused → crashed → gameover), the player, the vehicle
 * pool, the seeded threat spawner, a minimal ambient spawner, the difficulty
 * director, the scoring controller, a Rapier physics world, and the event
 * queue.
 *
 * Step order (only when phase === "playing"; crashed/gameover run the reduced
 * `stepDownedWorld` below, title/paused are inert aside from reset/resume):
 *   1. stepPlayer (lateral strafe + forward speed ramp + distance).
 *   2. Director snapshot at the current sim time → player forward speed is
 *      authoritative from the director (dual-speed ramp); we overwrite
 *      player.forwardSpeed with the director value so the ramp is exact.
 *   3. Threat spawner.update(distance, playerSpeed, director) → spawnVehicle
 *      each command into a free threat slot + create its Rapier kinematic body.
 *   4. Ambient spawner tick → spawnVehicle into ambient slots (no bodies).
 *   5. updateVehicles(pool, dt, LIVE playerSpeed, events) — dual-speed ramp.
 *   6. Sync every driving threat's kinematic Rapier body to its sim pose.
 *   7. Kick edge: pickKickTargets → applyKick (both targets on a DOUBLE KICK) (kinematic→dynamic + launch).
 *   8. Step Rapier (fixed dt) with an EventQueue (autoDrain).
 *   9. Mirror kicked bodies back into VehicleEntity (x,y,z + quat), then the
 *      ACTIVE TIP ENFORCEMENT: a grounded kicked body that is still lying flat
 *      (wheels- or roof-down) has its roll re-armed until it goes onto its side.
 *  10. Crash detection (analytic AABB player↔driving threat, plus the SQUEEZE
 *      rule for threading two adjacent-lane vehicles) → crashed.
 *  11. Near-miss detection (driving threat crosses z=0 with lateral gap in
 *      (contactExtent, nearMissMargin]) → award once.
 *  12. Kicked-vehicle touchdown → blink-out countdown (armed `wreckFadeDelay`
 *      after the ground hit, NOT at the settle) → kickedVehicleDown + despawn
 *      when it expires; the settle only freezes the pose. Out-of-bounds wrecks
 *      skip the countdown.
 *  13. Scoring: addDistance, recordKick/nearMiss, tickDecay, recomputeTotal.
 *  14. Phase transition: crashed → gameover after 2.4 s of sim time.
 *
 * Crashed / gameover phases: NOT inert — the world outlives the player (see
 * `stepDownedWorld`). Time advances, traffic keeps driving past the wreck at
 * its own absolute road speeds, kinematic bodies stay synced, Rapier keeps
 * stepping (so the thrown-rider body spawned in onCrash tumbles onto the
 * road and kicked vehicles keep flying), and settled wrecks are reclaimed.
 * Gameplay stays off: no spawns, no crash/near-miss checks, no scoring. The
 * rider pose is mirrored into state.crashCyclist for the render cinematic.
 *
 * Hitstop: on a connecting kick, freeze the sim for
 * `round(TUNING.kick.hitstop / TUNING.sim.dt)` steps — those steps return
 * immediately without advancing anything (deterministic in step-space).
 * TUNING.kick.hitstop is currently 0, so HITSTOP_STEPS is 0 and NO step is ever
 * frozen: the launch is instant on the connect frame and the kicked vehicle's z
 * decreases from the very next step. The mechanism is kept intact (not deleted)
 * so raising the constant restores the freeze without a code change.
 *
 * Determinism: same seed + same input sequence ⇒ identical stateHash at every
 * step. Rapier is stepped with a fixed timestep and no wall-clock input; the
 * RNG streams (threat spawner, kick spin, ambient spawner) are seeded from
 * contact events) so the critical gameplay logic is pure and reproducible.
 *
 * Zero per-frame allocation: the event queue is a reused array (cleared each
 * step), the body map is a fixed array, scratch vectors are pre-allocated.
 */

import type RAPIER from "@dimforge/rapier3d-compat";
import { mulberry32, type Rng } from "../core/rng";
import { TUNING } from "./tuning";
import { initPhysics } from "./physics";
import type {
  InputState,
  Sim,
  SimEvent,
  VehicleEntity,
  WorldState,
} from "./types";
import { createPlayerState, stepPlayer } from "./player";
import {
  createVehiclePool,
  despawnVehicle,
  isLive,
  laneCenterX,
  lateralGap,
  modelHeight,
  modelLength,
  modelWidth,
  overlapsPlayer,
  overlapsPlayerZ,
  resetVehiclePool,
  spawnVehicle,
  updateVehicles,
  vehicleAbsSpeed,
  type VehiclePool,
} from "./vehicles";
import { createSpawner, type Spawner } from "./spawner";
import { createDirector, type Director } from "./director";
import { createScoring, type Scoring } from "./scoring";
import { createCoinField, type CoinField } from "./coins";
import {
  applyKick,
  COLLISION_CRASH_CYCLIST,
  COLLISION_CRASH_WALL,
  COLLISION_PLAYER,
  COLLISION_STATIC,
  COLLISION_THREAT_DRIVING,
  frictionTumbleAxis,
  pickKickTargets,
  type BodyMap,
  type KickLaunch,
  type KickRigidBody,
} from "./kick";

const T = TUNING;
const P = T.player;
const R = T.road;
const TR = T.traffic;
const K = T.kick;
const SC = T.score;

/**
 * Hitstop expressed in whole sim steps. Zero while `TUNING.kick.hitstop` is 0 —
 * the gate in `step()` (`hitstopRemaining > 0`) then never fires, so a kick
 * costs no frozen steps at all.
 */
const HITSTOP_STEPS = Math.round(K.hitstop / T.sim.dt);
/** Sim seconds the crashed phase lingers before transitioning to gameover. */
const CRASH_TO_GAMEOVER = 2.4;
/** Near-miss lateral margin (surface-to-surface, metres). */
const NEAR_MISS_MARGIN = SC.nearMissMargin;
/** Guardrail offset beyond the corridor half-width (matches render world.ts). */
const RAIL_OFFSET = 0.15;
/** Total corridor width (lanes + shoulders) — guardrails sit just outside. */
const CORRIDOR_WIDTH = R.laneCount * R.laneWidth + 2 * R.shoulderWidth;
const CORRIDOR_HALF = CORRIDOR_WIDTH / 2;
/** Guardrail centre x (corridor edge + half rail thickness + offset). */
const RAIL_X = CORRIDOR_HALF + RAIL_OFFSET + 0.15;
/**
 * Invisible crash-containment wall centre x (just inside the guardrail boxes).
 *
 * The rails are only 0.9 m tall and a ragdoll thrown from the strafe clamp used
 * to sail straight over them, ending the run with the crash camera pointed at
 * empty ground. These walls are 4 m tall, span the whole corridor run length,
 * and are in the CRASH_WALL collision group — whose mask is CRASH_CYCLIST alone
 * — so ONLY the thrown rider ever touches them. Kicked vehicles still fly off
 * the road (deliberate).
 *
 * Placed just INSIDE the corridor edge rather than out at the rail: the render
 * model is far bigger than the 0.3 m-radius capsule it mirrors, so a capsule
 * stopped at the rail line still shows a rider draped over the guardrail. At
 * 4.0 m the capsule centre can reach 3.7 m and the posed model lands on the
 * asphalt/shoulder, which is what the fixed crash camera frames.
 */
const CRASH_WALL_X = CORRIDOR_HALF - 0.3;
/** Half-thickness / half-height of a containment wall (m). */
const CRASH_WALL_HALF_T = 0.1;
const CRASH_WALL_HALF_H = 2.0;
/**
 * Kicked-vehicle "settled" test — the wreck has come to REST.
 *
 * This used to be a height test (`v.y < 0.6`), which was dead code: `v.y`
 * mirrors the Rapier body CENTRE of a cuboid resting on the ground half-space,
 * and the lowest possible resting centre over TUNING.vehicleModels is the
 * sedan's 1.46/2 = 0.73 m. Nothing ever fell below 0.6, so the only despawn
 * path left was the out-of-bounds box — and a wreck that came to rest INSIDE
 * that box leaked its pool slot forever (kicked vehicles are skipped by
 * updateVehicles, so checkKickedLanded is their only reclaimer) while staying
 * rendered and, since kicked vehicles are not z-advanced, tracking the player.
 *
 * The rest test reads the body's own motion instead, so it fires for every
 * model at whatever height it happens to settle at, on or off the corridor.
 */
const KICK_REST_LIN = 0.4; // m/s
const KICK_REST_ANG = 0.6; // rad/s
/** Consecutive steps below both thresholds before the wreck counts as down. */
const KICK_REST_STEPS = 30; // 0.5 s at 60 Hz
/**
 * Maximum lateral centre-to-centre distance for two vehicles to count as
 * ADJACENT for the squeeze rule (see `checkCrash`). Every threat sits at an
 * exact lane centre, so the real separations are 0 (same lane), laneWidth
 * (2.7 m, adjacent) and 2 * laneWidth (5.4 m, the far pair). The 0.1 m slack
 * only guards float error; the far pair must never qualify, because the middle
 * lane between them is exactly where the spawner's guaranteed path runs and
 * riding it is correct play, not a squeeze.
 */
const SQUEEZE_MAX_SEPARATION = R.laneWidth + 0.1;
/** Kicked-vehicle out-of-bounds box (far past player or far laterally). */
const KICK_OOB_Z = 60;
const KICK_OOB_X = 40;

/**
 * Body up axis · world up, straight from a mirrored Rapier quaternion
 * [x, y, z, w]: +1 = wheels down, 0 = exactly on its side, -1 = on its roof.
 *
 * The sign matters to callers that care WHICH way a wreck is lying; the tip
 * enforcement and the settle refusal both use |value| instead, because both
 * "wheels down" and "roof down" are the flat, unflipped-looking rests the owner
 * rejected — a wreck has to end up on its SIDE.
 */
function upDotY(q: [number, number, number, number]): number {
  return 1 - 2 * (q[0] * q[0] + q[2] * q[2]);
}

/**
 * The vehicle's LONG axis (local +Z) in world space, from a mirrored Rapier
 * quaternion [x, y, z, w]. Used by the nose/tail constraint below.
 */
function longAxis(q: [number, number, number, number]): {
  x: number;
  y: number;
  z: number;
} {
  const [x, y, z, w] = q;
  return {
    x: 2 * (x * z + w * y),
    y: 2 * (y * z - w * x),
    z: 1 - 2 * (x * x + y * y),
  };
}

/**
 * |long axis · world up| — 0 = lying flat (side or roof), 1 = standing straight
 * up on its nose or tail.
 *
 * THE GEOMETRY HOLE THIS CLOSES (owner playtest 2026-08-03: "sometimes they land
 * and slide on a SHORT side (like the front), which is unrealistic"). The rest
 * guarantee constrained only the body's UP axis (`upDotY` vs TUNING.kick.tipDot),
 * so a wreck standing on its NOSE — up axis horizontal, long axis vertical —
 * PASSED as "on its side". A car-shaped box has exactly two stable rests, side
 * and roof, and BOTH have a near-horizontal long axis; a nose/tail pose is an
 * unstable balance that real sheet metal falls out of immediately. Constraining
 * both axes is what makes "on its side or roof" actually mean that.
 */
function longDotY(q: [number, number, number, number]): number {
  return Math.abs(longAxis(q).y);
}

/**
 * The axis to spin about to drive a wreck's long axis back toward HORIZONTAL —
 * the "force the pitch through" torque for a nose/tail stand.
 *
 * Derivation. For a unit long axis L and ω = c·normalize(Y × L) (Y = world up),
 * (ω × L)·Y = -c·|L_horizontal|², i.e. STRICTLY NEGATIVE: rotating about Y × L
 * always lowers L.y. Multiplying by `sign(L.y)` therefore always drives |L.y|
 * toward 0 — the pose is resolved by construction, whatever the wreck's heading.
 * Note Y × L = (L.z, 0, -L.x), the same shape as the friction-true axis
 * `normalize(up × v)` = (v.z, 0, -v.x) — the two coincide exactly when the long
 * axis's horizontal projection points along the travel direction.
 *
 * FRICTION-TRUE FIRST. When the friction axis is ALSO productive (it lowers
 * |L.y| iff sign(L.y)·(v · L_horizontal) > 0) it is preferred, so the forced
 * righting keeps the roof leading the direction of travel — the enforcement
 * never counter-rotates a sliding wreck just to fix its nose. Only when the
 * friction axis would push the nose DEEPER does this fall back to Y × L.
 *
 * Degenerate long axis (exactly vertical, |L_horizontal| ≈ 0) falls back to the
 * friction axis, which is never zero (`frictionTumbleAxis` has its own fallback).
 */
function noseRightAxis(
  q: [number, number, number, number],
  vx: number,
  vz: number,
): { x: number; z: number } {
  const L = longAxis(q);
  const fric = frictionTumbleAxis(vx, vz);
  const speed = Math.hypot(vx, vz);
  if (speed > 1e-6) {
    // Productive iff sign(L.y) · (v · L_horizontal) > 0 (see the derivation).
    const dot = (vx * L.x + vz * L.z) / speed;
    if (Math.sign(L.y) * dot > 0) return fric;
  }
  const h = Math.hypot(L.z, L.x);
  if (h < 1e-6) return fric;
  const s = Math.sign(L.y) || 1;
  return { x: (s * L.z) / h, z: (-s * L.x) / h };
}

/**
 * THE SIDE-ROLL AXIS — the horizontal projection of the body's own LONG axis,
 * signed so the roll lays the vehicle over the way it is already going.
 *
 * A vehicle that trips while sliding sideways goes over about its LONG axis: the
 * tyres grip, the body's momentum carries the top on, and it lands on a flank.
 * That is the only topple a high-CG box (truck, semi) can credibly perform, and
 * the commonest one for a car. The axis is the long axis flattened into the
 * ground plane; the SIGN comes from the friction-true axis `normalize(up × v)`
 * — the direction road friction is actually torquing the body — projected onto
 * it, so the wreck falls the way its slide is already tipping it.
 *
 * DEGENERATE CASE. When the velocity points along the body's long axis the
 * projection vanishes and there is no "which side" to be read off the slide.
 * The fallback continues whatever roll the body already has about that axis
 * (`av`, which carries the launch tilt seed), and failing that the launch's own
 * corridor-away sign passed in as `fallback` — deterministic either way.
 */
function sideRollAxis(
  q: [number, number, number, number],
  vx: number,
  vz: number,
  av: { x: number; y: number; z: number },
  fallback: number,
): { x: number; z: number } {
  const L = longAxis(q);
  const h = Math.hypot(L.x, L.z);
  if (h < 1e-6) return frictionTumbleAxis(vx, vz);
  const ax = L.x / h;
  const az = L.z / h;
  const fric = frictionTumbleAxis(vx, vz);
  let sign = fric.x * ax + fric.z * az;
  if (Math.hypot(vx, vz) < 1e-6 || Math.abs(sign) < 0.15) sign = av.x * ax + av.z * az;
  const s = sign > 0 ? 1 : sign < 0 ? -1 : fallback;
  return { x: s * ax, z: s * az };
}

// --- Kicked-vehicle FIRST GROUND IMPACT (the dust-plume cue) ----------------
/**
 * A kicked vehicle is considered to be genuinely FALLING once its vertical
 * velocity drops below this (m/s). Arms the impact detector; without the arm a
 * launch whose first frames are still rising would trip the "absorbed" test
 * below immediately.
 */
const KICK_FALL_ARM_VY = -1;
/**
 * Impact test (a): the fall has been ABSORBED — vertical velocity has climbed
 * back above this (m/s) after having been armed. Covers both a hard stop and a
 * bounce, without needing contact events.
 */
const KICK_IMPACT_VY = -0.5;
/**
 * Impact test (b): the body centre has dropped to within this (m) of the
 * resting height of its own model box (modelHeight/2). Catches the case where
 * the body slides along the ground at near-constant (still negative) vy.
 */
const KICK_IMPACT_Y_SLACK = 0.25;
/**
 * PER-IMPACT HAIRCUT ARM (m/s). A kicked wreck's slam detector re-arms whenever
 * its vertical velocity drops below this, and fires (charging
 * TUNING.kick.wreckImpactHaircut against the horizontal speed) when the fall is
 * absorbed. Unlike `kickedGroundHit` — a once-per-wreck latch that owns the dust
 * plume — this one re-arms for EVERY face-plant of the tumble, so each slam
 * visibly sheds energy instead of the wreck bouncing along at a constant skate.
 * Well below KICK_FALL_ARM_VY so contact jitter can never charge a wreck twice
 * for the same landing.
 */
const KICK_SLAM_ARM_VY = -3;

// --- Crash cyclist (thrown rider) dynamic body ------------------------------
/**
 * RIDER SHAPE (2026-08-02 crash-feel pass).
 *
 * OWNER BUG: "on cyclist crash, the physics is a bit weird when he falls on the
 * ground. Maybe the collision box is not detailed enough?" It was a CAPSULE —
 * a surface of revolution about its own long axis — so once the rider went down
 * he rolled along the asphalt like a dropped pill and kept spinning. A body
 * tangled in a bicycle does neither: it slaps down on a flat side and stops.
 *
 * It is now a ROUNDED BOX (Rapier `RoundCuboid`), still one collider:
 *  - flat faces, so it settles on a side instead of rolling;
 *  - rounded edges (CYC_BORDER), so it tumbles over them smoothly in the air
 *    and never catches a sharp corner and jitters on landing — the failure mode
 *    a plain Cuboid has;
 *  - roughly the tangle's silhouette: ~0.76 x 1.44 x 0.68 m overall, against a
 *    ~1.9 m model, with the long axis on local +Y (the rider's spine), which is
 *    the convention the render side already assumes when it rolls the settled
 *    model 90° about its length (render/crashSequence.ts).
 *
 * ONE collider, created on crash and removed on reset, exactly like before.
 * That is deliberate: a multi-collider body (capsule + head + shoulder balls)
 * reads even better, but Rapier recycles freed collider slots LIFO, so after a
 * reset the same crash solved in a different order and fresh-vs-reset
 * stateHashes drifted (regressions.test.ts). A single shape has no ordering to
 * get wrong.
 */
/** Half-extents of the rounded box, BEFORE the border radius is added. */
const CYC_HALF_X = 0.28;
const CYC_HALF_Y = 0.62;
const CYC_HALF_Z = 0.24;
/** Edge rounding (m). Outer half-extents are the above plus this. */
const CYC_BORDER = 0.1;
/** Spawn height of the body CENTER at the moment of impact. */
const CYC_SPAWN_Y = 1.0;
/**
 * Launch velocity: lateral (signed toward the crash vehicle), up, back (+Z).
 *
 * The throw is biased UP + BACK, with only a token lateral kick. The lateral
 * component used to be the corridor-scaled 2.7 m/s, which — thrown from the
 * strafe clamp (|x| 3.85 m) toward the outside — carried the ragdoll over the
 * 0.9 m guardrail and off the highway, leaving the crash camera framing empty
 * ground. 2.0 m/s keeps the tumble reading as a sideways spill without
 * committing it to the verge; the invisible CRASH-WALL colliders (see
 * `CRASH_WALL_X`) are the hard guarantee behind it.
 *
 * The BACK component is what the fixed crash camera is framed around: the rider
 * comes to rest ~5 m behind the impact point, which is why the camera parks at
 * z = +11 (render/crashSequence.ts CAM_FINAL_Z). Changing this changes what the
 * cinematic frames.
 */
const CYC_LAUNCH_LAT = 2.0;
/**
 * Up-throw (m/s). Was 8.0 — a 3.8 m apex and 1.5 s of hang time out of a 2.4 s
 * cinematic, i.e. the player spent most of the crash watching a body float and
 * only ~0.9 s watching it hit the road. The owner's note is about how the fall
 * LANDS, so the arc is now shorter (apex ~2.5 m, ~1.15 s airborne) to buy back
 * roughly half a second of ground contact. Still comfortably above the
 * "actually flew" floor the grounding tests assert (apex > spawn + 0.5 m).
 */
const CYC_LAUNCH_UP = 5.5;
const CYC_LAUNCH_BACK = 4.5;
/** Tumble angular velocity magnitude (rad/s). Trimmed with the shorter arc. */
const CYC_TUMBLE = 4.5;
/** Damping + friction so the rider skids to rest instead of sliding forever. */
const CYC_ANG_DAMPING = 1.5;
const CYC_LIN_DAMPING = 0.3;
const CYC_FRICTION = 1.2;
/**
 * Zero restitution: a body wrapped around a bicycle does not bounce. The old
 * 0.05 was small but it was enough to put a visible hop-and-jitter into the
 * first ground contact, which is the exact moment the crash cam is framed on.
 */
const CYC_RESTITUTION = 0.0;
/**
 * GROUNDED DAMPING. Above the road the rider tumbles freely (CYC_ANG_DAMPING);
 * once the body centre drops below this height it is in contact, and free
 * spinning there reads as ice. Angular damping is raised hard and linear
 * damping moderately so the tangle scrubs off its rotation in a few tenths of a
 * second and comes to rest instead of pirouetting on the asphalt.
 *
 * This is a pure function of the body's own translation, evaluated once per
 * fixed step in `syncCrashCyclist`, so it stays deterministic.
 */
const CYC_GROUNDED_Y = 0.62;
const CYC_GROUNDED_ANG_DAMPING = 6.0;
const CYC_GROUNDED_LIN_DAMPING = 1.2;
/** Settle thresholds (m/s and rad/s) — render eases to the flat pose after this. */
const CYC_SETTLE_LIN = 0.5;
const CYC_SETTLE_ANG = 1.3;

// ---------------------------------------------------------------------------
// Ambient spawner — minimal seeded scenic traffic outside the guardrails.
// Two directions, ambientLaneCount lanes per side, own signed frame velocity.
// ---------------------------------------------------------------------------

/** Initial values of the ambient distance cursors (metres of player travel). */
const AMBIENT_CURSOR_LEFT0 = -20;
const AMBIENT_CURSOR_RIGHT0 = -15;

interface AmbientSpawner {
  update(distance: number, dt: number, playerSpeed: number, rng: Rng): void;
  /**
   * Rewind the distance cursors to their construction values. MUST be called
   * from World.reset(): the cursors are keyed on `player.distance`, which
   * restarts at 0, so without a rewind the `while (next <= distance)` loops
   * never fire again until the new run exceeds the previous run's length —
   * i.e. zero scenic traffic for the whole retry.
   */
  reset(): void;
}

function createAmbientSpawner(pool: VehiclePool, events: SimEvent[]): AmbientSpawner {
  // Per-side per-direction cursor (metres of travel until next spawn).
  let nextLeft = AMBIENT_CURSOR_LEFT0;
  let nextRight = AMBIENT_CURSOR_RIGHT0;

  function spawnAmbient(
    side: -1 | 1,
    lane: number,
    oncoming: boolean,
    absSpeed: number,
    playerSpeed: number,
  ): void {
    const baseX = side * (CORRIDOR_HALF + TR.ambientLaneOffset + lane * 3.0);
    // Signed ABSOLUTE road speed: + = oncoming (toward the player), - = same
    // direction as the player. vehicles.ts integrates it as
    // `playerSpeed + speed`, so ambients track the player-speed ramp exactly
    // like the road surface does (a same-direction car slower than the player
    // correctly falls behind instead of racing away forever).
    const roadSpeed = oncoming ? absSpeed : -absSpeed;
    const z = oncoming ? -120 : -80;
    spawnVehicle(pool, {
      kind: "car",
      role: "ambient",
      lane: side * 10 + lane, // free id; not a corridor lane
      x: baseX,
      z,
      speed: roadSpeed,
      playerSpeed,
      yaw: oncoming ? Math.PI : 0,
    }, events);
  }

  /** Draw an absolute ambient road speed in [ambientSpeedMin, ambientSpeedMax]. */
  function drawSpeed(rng: Rng): number {
    return TR.ambientSpeedMin + rng() * (TR.ambientSpeedMax - TR.ambientSpeedMin);
  }

  function update(distance: number, dt: number, playerSpeed: number, rng: Rng): void {
    void dt;
    // Spawn cadence scales with ambient density and corridor speed.
    const gap = 100 / TR.ambientDensity; // metres per spawn per direction
    while (nextLeft <= distance) {
      const lane = Math.floor(rng() * TR.ambientLaneCount);
      const oncoming = rng() < 0.5;
      spawnAmbient(-1, lane, oncoming, drawSpeed(rng), playerSpeed);
      nextLeft += gap * (0.7 + rng() * 0.6);
    }
    while (nextRight <= distance) {
      const lane = Math.floor(rng() * TR.ambientLaneCount);
      const oncoming = rng() < 0.5;
      spawnAmbient(1, lane, oncoming, drawSpeed(rng), playerSpeed);
      nextRight += gap * (0.7 + rng() * 0.6);
    }
  }

  function reset(): void {
    nextLeft = AMBIENT_CURSOR_LEFT0;
    nextRight = AMBIENT_CURSOR_RIGHT0;
  }

  return { update, reset };
}

// ---------------------------------------------------------------------------
// World
// ---------------------------------------------------------------------------

/**
 * The sim world. Construct with `createWorld(seed)` (async, awaits Rapier
 * init) — or `createWorldSync(seed, RAPIER)` if the caller already has an
 * initialized RAPIER namespace (tests).
 */
export class World implements Sim {
  readonly state: WorldState;
  private readonly pool: VehiclePool;
  private spawner: Spawner;
  private readonly ambient: AmbientSpawner;
  private readonly director: Director;
  private readonly scoring: Scoring;
  /**
   * COINS (sim/coins.ts). Owned here, driven from `doStep`, and deliberately
   * DOWNSTREAM of the spawner: it is fed the spawn commands the spawner has
   * already produced and draws from its own seeded stream, so the traffic a
   * seed generates is bit-identical whether coins exist or not.
   */
  private readonly coins: CoinField;
  private readonly events: SimEvent[] = [];

  // Rapier (null until initialized; world.ts tests inject a RAPIER namespace).
  private rapier: typeof RAPIER | null = null;
  private physics: RAPIER.World | null = null;
  private eventQueue: RAPIER.EventQueue | null = null;
  private bodies: BodyMap = [];
  private bodyHandles: number[] = [];
  /** Per-threat-slot count of consecutive steps a kicked body has been at rest. */
  private kickedRestSteps: number[] = [];
  /**
   * Per-threat-slot first-ground-impact state (see KICK_FALL_ARM_VY et al).
   * `kickedFalling[i]` is 1 once the launched body has started genuinely
   * descending; `kickedGroundHit[i]` is 1 once the impact event has fired, so
   * later bounces never emit a second plume. Both are pre-sized in init() and
   * only ever written in place — zero allocation in steady state.
   */
  private kickedFalling: number[] = [];
  private kickedGroundHit: number[] = [];
  /**
   * SPEED RATCHET (see `enforceWreckGround`): the highest HORIZONTAL speed (m/s)
   * this slot's wreck is still allowed after touchdown. Seeded at the wreck's
   * landing speed and lowered - never raised - every step, so no held rotation
   * can turn into a motor that carries the wreck further than the physics would.
   * Infinity while the slot is not a landed wreck (the clamp is then inert).
   */
  private kickedSpeedCap: number[] = [];
  /**
   * THE TOPPLE STATE (see `enforceWreckGround` and TUNING.kick.topple*).
   *
   * `kickedGroundTime[i]` - seconds since touchdown; the spin-out runs for
   *   `toppleDelay` of it before the topple begins.
   * `kickedTopple[i]`     - the mode drawn at launch: 1 = sideRoll (about the
   *   body's long axis; always this for trucks/semis), 0 = trip (about the
   *   friction-true axis, i.e. over the leading edge - cars only).
   * `kickedYawSign[i]`    - the spin-out direction the launch chose (derived
   *   from the contact side, see kick.ts `computeLaunch`).
   * `kickedToppleTime[i]` / `kickedToppleRot[i]` - driven seconds and driven
   *   radians, against `toppleMaxTime` / `toppleMaxRot`. Either bound retires
   *   the topple; the budget is what makes "at most one topple" structural.
   * `kickedToppleDone[i]` - 1 once the wreck is on its side/roof (or the bounds
   *   fired). The controller never touches it again and its angular damping is
   *   raised to `toppleSpentDamping`, so it cannot re-flip.
   */
  private kickedGroundTime: number[] = [];
  private kickedTopple: number[] = [];
  private kickedYawSign: number[] = [];
  private kickedToppleTime: number[] = [];
  private kickedToppleRot: number[] = [];
  private kickedToppleDone: number[] = [];
  /**
   * ROLL DIRECTION armed at launch (+1 / -1) - the fallback sign for a side-roll
   * topple whose velocity is too aligned with the body's long axis to pick a
   * side from (see `sideRollAxis`). Chosen so the wreck goes over AWAY from the
   * corridor, matching the launch's own lateral throw.
   */
  private kickedTipSign: number[] = [];
  /** Seconds of flat-rest settle REFUSAL already spent (capped at tipMaxTime). */
  private kickedTipTime: number[] = [];
  private kickedRefuseTime: number[] = [];
  /**
   * NOSE/TAIL-STAND state (TUNING.kick.noseStandDot / noseGrindTime /
   * noseForceRate / noseMaxTime).
   *
   * `kickedNoseTime[i]` is the CONTINUOUS run of seconds this wreck's long axis
   * has been past `noseStandDot` — it resets to 0 the moment the pose comes back
   * down, so an ordinary flip passing through vertical never trips the forced
   * righting. `kickedNoseSpent[i]` is the LIFETIME total of forced-righting
   * enforcement, capped at `noseMaxTime` (its own budget, separate from
   * `kickedTipTime`, so the two refusals cannot starve each other).
   * `kickedNoseRefuse[i]` is the matching settle-refusal clock.
   */
  private kickedNoseTime: number[] = [];
  private kickedNoseSpent: number[] = [];
  private kickedNoseRefuse: number[] = [];
  /**
   * PER-IMPACT HAIRCUT ARM (see KICK_SLAM_ARM_VY and
   * TUNING.kick.wreckImpactHaircut): 1 once this wreck is falling hard enough
   * that the next absorbed landing should shed energy. Re-arms every face-plant.
   */
  private kickedSlamArmed: number[] = [];
  /**
   * POSE FREEZE (`checkKickedLanded`): 1 once this wreck has SETTLED — its
   * velocities are zeroed, its body slept, and both the ground controller
   * (`enforceWreckGround`) and the rest test skip it for the rest of its life.
   *
   * This used to be `wreckFade > 0` ("blinking out ⇒ the pose is final"), which
   * was only true while the blink was armed BY the settle. The blink now starts
   * `wreckFadeDelay` after touchdown, with the wreck still grinding, so the two
   * had to be split: the fade is a despawn clock, this flag is the freeze.
   */
  private kickedFrozen: number[] = [];
  /**
   * Scratch index buffer for `checkCrash`'s squeeze rule: the threat slots
   * whose vehicle currently spans the bike's z-window. Pre-sized to the threat
   * pool in the constructor and only ever written in place, so the per-step
   * crash test allocates nothing.
   */
  private readonly zOverlapIdx: number[];
  /**
   * Per-spawn-command result: the vehicle `spawnVehicle` produced this frame,
   * or null if the pool dropped the command. Written in step 3 and read in step
   * 3b (the coin hook, which needs the ENTITY — its model length is what the
   * co-arrival solve pairs against). Grown once to the largest command batch
   * ever seen and then reused — the opening frame emits the whole horizon at
   * once, so this settles within one run. The references are cleared implicitly
   * on the next frame's write; a stale entry is never read, because 3b only
   * walks the same `cmds` range 3 just wrote.
   */
  private readonly spawnedCmd: (VehicleEntity | null)[] = [];
  private playerBody: KickRigidBody | null = null;
  private hitstopRemaining = 0;
  private crashTime: number | null = null;
  private crashVehicleId = -1;
  /** Lateral offset (vehicle.x - player.x) of the crash vehicle, for the launch side. */
  private crashVehicleDx = 0;
  /** The thrown-rider dynamic body (null outside the crashed/gameover phases). */
  private crashCyclistBody: RAPIER.RigidBody | null = null;
  private kickRng: Rng = mulberry32(0);
  private ambientRng: Rng = mulberry32(0);


  constructor(seed: number) {
    this.pool = createVehiclePool(TR.maxThreatVehicles, TR.maxAmbientVehicles);
    this.zOverlapIdx = new Array(TR.maxThreatVehicles).fill(0);
    this.spawner = createSpawner(seed);
    this.ambient = createAmbientSpawner(this.pool, this.events);
    this.director = createDirector();
    this.scoring = createScoring();
    this.coins = createCoinField(seed);

    const player = createPlayerState();

    this.state = {
      phase: "title",
      time: 0,
      seed,
      player,
      vehicles: this.pool.slots,
      coins: this.coins.coins,
      score: this.scoring.state,
      events: this.events,
      crashCyclist: null,
    };

    this.kickRng = mulberry32(seed ^ 0x5eed);
    this.ambientRng = mulberry32(seed ^ 0xa7b);
  }

  // -------------------------------------------------------------------------
  // Rapier setup
  // -------------------------------------------------------------------------

  /** Initialize Rapier physics with static structure + player + threat bodies. */
  init(rapier: typeof RAPIER): void {
    this.rapier = rapier;
    const world = new rapier.World({ x: 0, y: -9.81, z: 0 });
    world.timestep = T.sim.dt;
    this.physics = world;
    this.eventQueue = new rapier.EventQueue(true);

    // --- Static ground plane (y = 0, normal +Y). ---
    world.createCollider(
      new rapier.ColliderDesc(new rapier.HalfSpace({ x: 0, y: 1, z: 0 })).setCollisionGroups(
        COLLISION_STATIC,
      ),
    );

    // --- Static guardrail cuboids at ±RAIL_X (full corridor run length). ---
    const railLen = (R.chunksAhead + R.chunksBehind) * R.chunkLength;
    const railHy = R.guardrailHeight / 2;
    world.createCollider(
      new rapier.ColliderDesc(new rapier.Cuboid(0.15, railHy, railLen / 2))
        .setTranslation(-RAIL_X, railHy, 0)
        .setCollisionGroups(COLLISION_STATIC),
    );
    world.createCollider(
      new rapier.ColliderDesc(new rapier.Cuboid(0.15, railHy, railLen / 2))
        .setTranslation(RAIL_X, railHy, 0)
        .setCollisionGroups(COLLISION_STATIC),
    );

    // --- Invisible crash-containment walls (CRASH_CYCLIST group only). ---
    for (const sign of [-1, 1]) {
      world.createCollider(
        new rapier.ColliderDesc(
          new rapier.Cuboid(CRASH_WALL_HALF_T, CRASH_WALL_HALF_H, railLen / 2),
        )
          .setTranslation(sign * CRASH_WALL_X, CRASH_WALL_HALF_H, 0)
          .setCollisionGroups(COLLISION_CRASH_WALL),
      );
    }

    // --- Player kinematic capsule body. ---
    const playerBody = world.createRigidBody(
      rapier.RigidBodyDesc.kinematicPositionBased()
        .setTranslation(0, P.capsuleHalfHeight + P.capsuleRadius, 0)
        .setEnabled(false),
    );
    world.createCollider(
      new rapier.ColliderDesc(
        new rapier.Capsule(P.capsuleHalfHeight, P.capsuleRadius),
      ).setCollisionGroups(COLLISION_PLAYER),
      playerBody,
    );
    this.playerBody = playerBody;

    // --- Threat kinematic cuboid bodies (one per threat slot, disabled until spawned). ---
    this.bodies = new Array(this.pool.maxThreat).fill(null);
    this.bodyHandles = new Array(this.pool.maxThreat).fill(-1);
    this.kickedRestSteps = new Array(this.pool.maxThreat).fill(0);
    this.kickedFalling = new Array(this.pool.maxThreat).fill(0);
    this.kickedGroundHit = new Array(this.pool.maxThreat).fill(0);
    this.kickedSpeedCap = new Array(this.pool.maxThreat).fill(Infinity);
    this.kickedGroundTime = new Array(this.pool.maxThreat).fill(0);
    this.kickedTopple = new Array(this.pool.maxThreat).fill(0);
    this.kickedYawSign = new Array(this.pool.maxThreat).fill(1);
    this.kickedToppleTime = new Array(this.pool.maxThreat).fill(0);
    this.kickedToppleRot = new Array(this.pool.maxThreat).fill(0);
    this.kickedToppleDone = new Array(this.pool.maxThreat).fill(0);
    this.kickedTipSign = new Array(this.pool.maxThreat).fill(1);
    this.kickedTipTime = new Array(this.pool.maxThreat).fill(0);
    this.kickedRefuseTime = new Array(this.pool.maxThreat).fill(0);
    this.kickedNoseTime = new Array(this.pool.maxThreat).fill(0);
    this.kickedNoseSpent = new Array(this.pool.maxThreat).fill(0);
    this.kickedNoseRefuse = new Array(this.pool.maxThreat).fill(0);
    this.kickedSlamArmed = new Array(this.pool.maxThreat).fill(0);
    this.kickedFrozen = new Array(this.pool.maxThreat).fill(0);
    for (let i = 0; i < this.pool.maxThreat; i++) {
      const body = world.createRigidBody(
        rapier.RigidBodyDesc.kinematicPositionBased().setEnabled(false),
      );
      // Placeholder car cuboid; resized on spawn.
      world.createCollider(
        new rapier.ColliderDesc(
          new rapier.Cuboid(TR.carWidth / 2, TR.carHeight / 2, TR.carLength / 2),
        ).setCollisionGroups(COLLISION_THREAT_DRIVING),
        body,
      );
      this.bodies[i] = body;
      this.bodyHandles[i] = body.handle;
    }
  }

  // -------------------------------------------------------------------------
  // Sim interface
  // -------------------------------------------------------------------------

  step(input: InputState): void {
    const st = this.state;

    // Title: any input starts a run.
    if (st.phase === "title") {
      if (input.left || input.right || input.kick) {
        this.startRun();
      }
      return;
    }

    // Paused: inert (resume handled by resume()).
    if (st.phase === "paused") return;

    // Crashed / gameover: the world OUTLIVES the player (see stepDownedWorld).
    if (st.phase === "crashed") {
      this.stepDownedWorld();
      if (this.crashTime !== null && st.time - this.crashTime >= CRASH_TO_GAMEOVER) {
        st.phase = "gameover";
      }
      return;
    }
    if (st.phase === "gameover") {
      this.stepDownedWorld();
      return;
    }

    // --- Playing. ---
    // Hitstop: freeze everything for N steps after a connecting kick.
    if (this.hitstopRemaining > 0) {
      this.hitstopRemaining -= 1;
      return;
    }

    this.doStep(input);
  }

  /**
   * The step run during BOTH the `crashed` and `gameover` phases — the world
   * keeps living after the rider goes down.
   *
   * Everything that makes the road look ALIVE runs: time advances, traffic
   * keeps driving (at playerSpeed 0 — the rider is down, so vehicles close at
   * their own ABSOLUTE road speeds), their kinematic bodies stay synced, the
   * Rapier world steps (so the thrown rider tumbles and kicked wrecks keep
   * flying), and settled wrecks are still reclaimed.
   *
   * Everything that is GAMEPLAY stays off: no spawner updates (player.distance
   * is frozen, so the already-planned stream simply drains past over the next
   * ~10 s and then the road empties — which is fine, the cinematic is 2.4 s and
   * the gameover panel sits over it), no crash/near-miss checks, no scoring.
   *
   * Determinism is untouched: fixed timestep, no RNG draws, no wall clock.
   */
  private stepDownedWorld(): void {
    const st = this.state;
    // Clear the queue like doStep does. Without this the crashed phase kept
    // re-presenting the LAST playing step's events (the crash itself, and any
    // kick/ground-hit that came with it) on every one of its ~144 steps, so a
    // consumer counting events saw one plume as a hundred.
    this.events.length = 0;
    st.time += T.sim.dt;
    // Traffic keeps flowing past the downed rider (playerSpeed 0).
    updateVehicles(this.pool, T.sim.dt, 0, this.events);
    this.syncDrivingBodies();
    if (this.physics && this.eventQueue) {
      this.physics.step(this.eventQueue);
    }
    this.syncKickedBodies();
    this.enforceWreckGround();
    this.syncCrashCyclist();
    this.checkKickedLanded();
  }

  /** The core playing step. */
  private doStep(input: InputState): void {
    const st = this.state;
    const dt = T.sim.dt;
    this.events.length = 0;


    // 1. Director: authoritative forward speed (dual-speed ramp). Set this
    //    BEFORE stepPlayer so distance integrates with the correct speed.
    st.time += dt;
    const dir = this.director.at(st.time);
    st.player.forwardSpeed = dir.playerSpeed;

    // 2. Player lateral movement + distance (stepPlayer integrates distance
    //    with the now-correct forwardSpeed).
    stepPlayer(st.player, input, dt);

    // 3. Threat spawns.
    const cmds = this.spawner.update(st.player.distance, st.player.forwardSpeed, dir);
    for (let c = 0; c < cmds.length; c++) {
      const cmd = cmds[c];
      const v = spawnVehicle(this.pool, {
        kind: cmd.kind,
        role: "threat",
        lane: cmd.lane,
        // Pure arcade grid: every threat sits at an exact lane centre.
        x: laneCenterX(cmd.lane),
        z: cmd.z,
        speed: cmd.speed,
        playerSpeed: st.player.forwardSpeed,
        yaw: Math.PI, // oncoming → faces +Z toward player
      }, this.events);
      if (v) this.onThreatSpawned(v);
      // Remember WHICH vehicle this command became — see 3b. Null for a command
      // the pool dropped.
      this.spawnedCmd[c] = v;
    }
    // 3b. COIN CHAINS (sim/coins.ts). Hooked on the COMMITTED placements — the
    //     point where a vehicle is definitely going into this lane at this
    //     arrival, which is exactly the guarantee the "never in an empty lane"
    //     rule needs.
    //
    //     A SECOND pass over the same commands, in the same order, deliberately:
    //     the chain's overlap clamp has to see EVERY vehicle this frame put on
    //     the road, not just the ones committed before it. Frame one emits the
    //     whole opening pipeline at once (spawner.ts header 7), so interleaving
    //     the two let a chain from an early command land 5.8 m behind a vehicle
    //     a later command had not spawned yet. The rng draw order is the command
    //     order either way, so nothing about determinism changes — and the
    //     spawner's own stream is untouched regardless: this reads commands it
    //     has already produced and draws only from the coin rng.
    for (let c = 0; c < cmds.length; c++) {
      // A command the POOL DROPPED (spawnVehicle returns null when every slot
      // is live) never became a vehicle, so a chain laid off it would end in
      // front of nothing — the one way this hook could produce the empty lane
      // the spec forbids. Skip it entirely; the coin rng is independent of the
      // spawner's, so consuming fewer draws here cannot move the traffic.
      const sponsor = this.spawnedCmd[c];
      if (!sponsor) continue;
      // The SPONSOR ENTITY, not the command: the co-arrival solve pairs the
      // chain with the vehicle's FRONT, and the model (hence its length) is
      // only decided inside spawnVehicle.
      this.coins.onVehicleSpawn(
        sponsor,
        st.player.forwardSpeed + cmds[c].speed,
        st.player.forwardSpeed,
        st.time,
        this.pool.slots,
      );
    }

    // 4. Ambient spawns (scenic, no bodies).
    this.ambient.update(
      st.player.distance,
      dt,
      st.player.forwardSpeed,
      this.ambientRng,
    );

    // 5. Advance vehicles with the LIVE player speed (dual-speed ramp).
    updateVehicles(this.pool, dt, st.player.forwardSpeed, this.events);

    // 5b. Coins. They are ROAD-FIXED, so they close at exactly the player's own
    //     forward speed (vehicles close at playerSpeed + vehicleSpeed — see
    //     sim/coins.ts). Collection is instant and deterministic; the magnet
    //     "suck" is render-only.
    this.coins.update(dt, st.player.forwardSpeed);
    this.coins.collect(st.player.x, st.time, this.events, this.scoring);

    // 6. Sync driving threat kinematic bodies to sim poses.
    this.syncDrivingBodies();

    // 7. Kick edge.
    if (input.kick && st.player.kickCooldownRemaining <= 0) {
      this.tryKick();
    }

    // 8. Step Rapier (fixed dt) — integrates kicked dynamic bodies.
    if (this.physics && this.eventQueue) {
      this.physics.step(this.eventQueue);
    }

    // 9. Mirror kicked bodies back into VehicleEntity, then close the loop on
    //    the guaranteed flip (a grounded wreck that is still upright gets its
    //    roll re-armed until it goes over).
    this.syncKickedBodies();
    this.enforceWreckGround();

    // 10. Crash detection (analytic AABB player ↔ driving threat).
    if (this.checkCrash()) {
      this.onCrash();
      return;
    }

    // 11. Near-miss detection (driving threat crosses z=0).
    this.checkNearMiss();

    // 12. Kicked-vehicle landing / out-of-bounds.
    this.checkKickedLanded();

    // 13. Scoring.
    this.scoring.addDistance(st.player.forwardSpeed * dt);
    this.scoring.tickDecay(st.time, dt);
    this.scoring.recomputeTotal();
  }

  // -------------------------------------------------------------------------
  // Spawn / body lifecycle
  // -------------------------------------------------------------------------

  /** Create/resize the Rapier body for a freshly spawned threat. */
  private onThreatSpawned(v: VehicleEntity): void {
    if (!this.rapier || !this.physics) return;
    const rapier = this.rapier;
    const handle = this.bodyHandles[v.id];
    if (handle < 0) return;
    const rigidBody = this.physics.getRigidBody(handle);
    // Resize: remove existing colliders, create a fresh sized cuboid for the kind.
    const colCount = rigidBody.numColliders();
    const toRemove: RAPIER.Collider[] = [];
    for (let i = 0; i < colCount; i++) toRemove.push(rigidBody.collider(i));
    for (const c of toRemove) this.physics.removeCollider(c, false);
    // Collider dims come from the VISIBLE model, not the kind, so the box the
    // physics uses is the box the player sees.
    const halfW = modelWidth(v.model) / 2;
    const halfH = modelHeight(v.model) / 2;
    const halfL = modelLength(v.model) / 2;
    const col = new rapier.ColliderDesc(
      new rapier.Cuboid(halfW, halfH, halfL),
    ).setCollisionGroups(COLLISION_THREAT_DRIVING);
    this.physics.createCollider(col, rigidBody);
    // Position + enable the kinematic body at the spawn pose.
    rigidBody.setBodyType(rapier.RigidBodyType.KinematicPositionBased, false);
    rigidBody.setTranslation({ x: v.x, y: halfH, z: v.z }, false);
    rigidBody.setRotation(
      { x: 0, y: Math.sin(v.yaw / 2), z: 0, w: Math.cos(v.yaw / 2) },
      false,
    );
    rigidBody.setEnabled(true);
  }

  /** Sync every live driving threat's kinematic body to its current sim pose. */
  private syncDrivingBodies(): void {
    if (!this.physics) return;
    const slots = this.pool.slots;
    for (let i = 0; i < this.pool.maxThreat; i++) {
      const v = slots[i];
      if (!isLive(v) || v.motion !== "driving") continue;
      const handle = this.bodyHandles[i];
      if (handle < 0) continue;
      const halfH = modelHeight(v.model) / 2;
      const rb = this.physics.getRigidBody(handle);
      // Use setNextKinematicTranslation so Rapier computes a kinematic velocity
      // for one-way interaction with any dynamic (kicked) body.
      rb.setNextKinematicTranslation({ x: v.x, y: halfH, z: v.z });
      rb.setNextKinematicRotation(
        { x: 0, y: Math.sin(v.yaw / 2), z: 0, w: Math.cos(v.yaw / 2) },
      );
    }
  }

  /**
   * Mirror kicked dynamic bodies back into their VehicleEntity (pose + quat)
   * AND detect each wreck's first ground impact.
   *
   * Impact detection is a pure read of the body's own velocity + height (no
   * Rapier contact events), so it stays deterministic under the fixed-step
   * integration and costs one branch per kicked slot:
   *   1. arm  — vertical velocity falls below KICK_FALL_ARM_VY (genuinely
   *             descending, so the rising half of the launch can't trip it),
   *   2. fire — the fall is absorbed (vy back above KICK_IMPACT_VY) OR the
   *             centre reaches its own resting height (modelHeight/2 plus a
   *             little slack, for wrecks that skid in at constant vy).
   * `kickedGroundHit` latches so bounces never emit a second plume.
   */
  private syncKickedBodies(): void {
    if (!this.physics) return;
    const slots = this.pool.slots;
    for (let i = 0; i < this.pool.maxThreat; i++) {
      const v = slots[i];
      if (!isLive(v) || v.motion !== "kicked") continue;
      const handle = this.bodyHandles[i];
      if (handle < 0) continue;
      const rb = this.physics.getRigidBody(handle);
      const t = rb.translation();
      const r = rb.rotation();
      v.x = t.x;
      v.y = t.y;
      v.z = t.z;
      v.quat = [r.x, r.y, r.z, r.w];

      // NOSE/TAIL-STAND CLOCK. The CONTINUOUS run of grounded time this wreck's
      // long axis has spent past `noseStandDot` (≈45° from horizontal). Kept
      // here, in the one loop that visits every kicked body with no early exit,
      // so the two enforcements below read a clock that can never skip a frame.
      // It resets the instant the pose comes back down, so an ordinary flip
      // passing through vertical is never mistaken for a nose STAND.
      if (this.kickedGroundHit[i] && v.y <= this.wreckGroundedY(v) &&
          longDotY(v.quat) > K.noseStandDot) {
        this.kickedNoseTime[i] += T.sim.dt;
      } else {
        this.kickedNoseTime[i] = 0;
      }

      // PER-IMPACT ENERGY HAIRCUT (TUNING.kick.wreckImpactHaircut). Runs for
      // EVERY slam of the wreck's life, not just the first: arm on a genuine
      // fall (vy < KICK_SLAM_ARM_VY), fire when that fall is absorbed, and take
      // a fixed bite out of the HORIZONTAL speed on the frame of impact.
      // Vertical is untouched so bounces still read. Without it the wreck only
      // ever lost speed to steady friction, so each face-plant looked elastic
      // and the wreck skated; with it the wreck ARRIVES, SLAMS, sheds, and
      // grinds down. Deterministic and rng-free.
      const slamV = rb.linvel();
      if (!this.kickedSlamArmed[i]) {
        if (slamV.y < KICK_SLAM_ARM_VY) this.kickedSlamArmed[i] = 1;
      } else if (slamV.y > KICK_IMPACT_VY) {
        this.kickedSlamArmed[i] = 0;
        const keep = K.wreckImpactHaircut;
        rb.setLinvel(
          { x: slamV.x * keep, y: slamV.y, z: slamV.z * keep },
          true,
        );
      }

      if (this.kickedGroundHit[i]) {
        // THE TOUCHDOWN CLOCK. Advanced HERE — the one loop that visits every
        // kicked body every step with no early exit — rather than inside
        // `enforceWreckGround`, which now stops running for a wreck the moment
        // it freezes. The blink-out is armed off this clock
        // (TUNING.kick.wreckFadeDelay), so a wreck that settles inside the delay
        // must still reach its despawn; a clock that stopped with the controller
        // would have leaked the slot forever.
        //
        // Identical to the old placement for the topple: `syncKickedBodies`
        // always runs immediately before `enforceWreckGround`, so the
        // `>= toppleDelay` comparison still sees the same value on the same step.
        this.kickedGroundTime[i] += T.sim.dt;
        continue;
      }
      const vy = rb.linvel().y;
      if (!this.kickedFalling[i]) {
        if (vy < KICK_FALL_ARM_VY) this.kickedFalling[i] = 1;
        continue;
      }
      const restY = modelHeight(v.model) / 2 + KICK_IMPACT_Y_SLACK;
      if (vy > KICK_IMPACT_VY || t.y < restY) {
        this.kickedFalling[i] = 0;
        this.kickedGroundHit[i] = 1;
        // Seed the ground-tumble RATCHET at the speed it actually landed with
        // (see `enforceWreckGround`): from here the wreck may only slow down.
        const lv = rb.linvel();
        this.kickedSpeedCap[i] = Math.hypot(lv.x, lv.z);
        this.kickedGroundTime[i] = 0;
        // TOUCHDOWN: drop the airborne AIR DRAG (kick.applyKick sets
        // TUNING.kick.wreckAirDamping so the flight visibly decelerates) back to
        // the ground value. On the asphalt the wreck's only speed loss must come
        // from TUNING.kick.wreckFriction, or the long approved backward skid —
        // and the smoke trail that rides on it — is damped away before it reads.
        rb.setLinearDamping(T.kick.wreckLinearDamping);
        this.events.push({
          type: "kickedVehicleGroundHit",
          vehicleId: v.id,
          x: v.x,
          z: v.z,
        });
      }
    }
  }

  // -------------------------------------------------------------------------
  // Kick
  // -------------------------------------------------------------------------

  private tryKick(): void {
    const st = this.state;
    // The kick window is sized per candidate from that vehicle's own live
    // closing speed (player.forwardSpeed + vehicleAbsSpeed(v)) inside
    // pickKickTarget — a constant window in TIME, floored at K.windowAhead
    // metres. `st.player.forwardSpeed` is passed as the closing-speed floor so
    // the window never collapses for a stationary/absent traffic speed.
    const targets = pickKickTargets(this.pool, st.player, st.player.forwardSpeed);
    const target = targets?.primary ?? null;
    if (!target) {
      // Whiff: the SAME price as a connect (whiffCooldown === cooldown). The
      // kick is a scarce resource, so a miss has to cost what a hit costs —
      // otherwise pressing early "just in case" is free.
      st.player.kickCooldownRemaining = K.whiffCooldown;
      st.player.lastKickLeg = null;
      this.events.push({ type: "kickWhiff" });
      return;
    }
    const dx = target.vehicle.x - st.player.x;
    // Live closing speed (dual-speed ramp): the launch reverses at least this
    // fast, so a kick keeps reading as a launch at every point on the ramp.
    const closing = st.player.forwardSpeed + vehicleAbsSpeed(target.vehicle);
    const launch = applyKick(target, st.time, dx, this.kickRng, this.bodies, closing);
    if (!launch) return; // defensive: no body

    // DOUBLE KICK: the player was straddling a lane boundary with a windowed
    // threat in EACH straddled lane, close together in z (sim/kick.ts
    // `pickKickTargets`). Both vehicles launch — each with its own
    // `computeLaunch` draw off the same kick rng, so the pair stays
    // deterministic — and both are scored, for ONE cooldown.
    const second = targets?.secondary ?? null;
    let secondLaunched = false;
    if (second) {
      const dx2 = second.vehicle.x - st.player.x;
      const closing2 = st.player.forwardSpeed + vehicleAbsSpeed(second.vehicle);
      const launch2 = applyKick(
        second, st.time, dx2, this.kickRng, this.bodies, closing2,
      );
      secondLaunched = launch2 !== null;
      if (launch2) this.armWreck(second.vehicle.id, dx2, launch2);
    }

    st.player.kickCooldownRemaining = K.cooldown;
    st.player.lastKickLeg = target.leg;
    // NOTE: nothing is done to the kicked vehicle's coin chain, deliberately —
    // a kicked sponsor leaves its coins as LOOT for the kick (sim/coins.ts
    // header 2b). In practice the chain has already gone past by then.
    this.armWreck(target.vehicle.id, dx, launch);
    if (second && secondLaunched) {
      this.events.push({
        type: "kick",
        vehicleId: target.vehicle.id,
        leg: target.leg,
        contactX: target.contactX,
        contactZ: target.contactZ,
        aimX: target.aimX,
        aimZ: target.aimZ,
        double: true,
        secondVehicleId: second.vehicle.id,
        secondLeg: second.leg,
        secondContactX: second.contactX,
        secondContactZ: second.contactZ,
        secondAimX: second.aimX,
        secondAimZ: second.aimZ,
      });
    } else {
      this.events.push({
        type: "kick",
        vehicleId: target.vehicle.id,
        leg: target.leg,
        contactX: target.contactX,
        contactZ: target.contactZ,
        aimX: target.aimX,
        aimZ: target.aimZ,
      });
    }
    // Scoring + hitstop. A double kick banks BOTH vehicles (two kick awards,
    // two multiplier bumps) — the existing per-kick scoring, applied twice.
    this.scoring.recordKick(target.vehicle.kind, st.time);
    if (second && secondLaunched) this.scoring.recordKick(second.vehicle.kind, st.time);
    this.hitstopRemaining = HITSTOP_STEPS;
  }

  // -------------------------------------------------------------------------
  // Crash + near-miss + landing
  // -------------------------------------------------------------------------

  /**
   * Crash test — two rules.
   *
   * (a) CONTACT. Analytic AABB overlap: the VISIBLE bike (front wheel plane →
   *     rider back, handlebar half-width minus forgiveness) vs each driving
   *     threat's VISIBLE model box. See vehicles.ts `overlapsPlayer`.
   *
   * (b) SQUEEZE. You may not thread the gap between two vehicles in ADJACENT
   *     lanes. The contact bands are deliberately narrower than the art
   *     (`contact.forgiveness`), so a player parked exactly on a lane boundary
   *     (x = ±1.35) sits 1.35 m from both neighbouring lane centres while the
   *     widest CAR band reaches only 1.125 m — a ~0.45 m corridor through which
   *     a player could ride straight between two blockers and never touch
   *     either. That defeats the whole point of the weave: the guaranteed path
   *     lane is the answer, not the crack between two blocked lanes. So if two
   *     driving threats BOTH span the bike's z-window right now, their lane
   *     separation is adjacent (<= laneWidth + 0.1 m, i.e. one lane pitch —
   *     never the 5.4 m far pair), and the player's x lies STRICTLY between
   *     their centres, that is a crash, attributed to the laterally nearer of
   *     the two.
   *
   *     Note this can only fire where rule (a) already ALMOST fires — the
   *     player is within one lane of two vehicles that are alongside it — so it
   *     tightens an unintended escape hatch rather than adding a new hazard.
   *     The greedy dodger rides lane CENTRES and only crosses lanes while the
   *     vehicles involved are far outside the z-window, so it never trips this
   *     (pinned by the solvability sweeps in spawner.test.ts).
   *
   * Zero allocation: the z-overlap candidate list is a pre-sized index scratch
   * buffer, and the pair loop is over at most `maxThreat` (24) entries.
   */
  private checkCrash(): boolean {
    const st = this.state;
    const px = st.player.x;
    const slots = this.pool.slots;
    const cand = this.zOverlapIdx;
    let n = 0;
    for (let i = 0; i < this.pool.maxThreat; i++) {
      const v = slots[i];
      if (!isLive(v) || v.motion !== "driving" || v.role !== "threat") continue;
      // (a) direct contact.
      if (overlapsPlayer(v, px)) {
        this.crashVehicleId = v.id;
        this.crashVehicleDx = v.x - px;
        return true;
      }
      // Candidate for (b): alongside the bike, but laterally clear of it.
      if (overlapsPlayerZ(v)) cand[n++] = i;
    }
    // (b) squeeze between two adjacent-lane vehicles.
    for (let a = 0; a < n; a++) {
      const vi = slots[cand[a]];
      for (let b = a + 1; b < n; b++) {
        const vj = slots[cand[b]];
        if (Math.abs(vi.x - vj.x) > SQUEEZE_MAX_SEPARATION) continue;
        const lo = vi.x < vj.x ? vi.x : vj.x;
        const hi = vi.x < vj.x ? vj.x : vi.x;
        if (px <= lo || px >= hi) continue; // not between them (same lane => never)
        // Attribute the crash to whichever of the two is laterally nearer, so
        // the ragdoll launch side and the crash event read correctly.
        const near = Math.abs(vi.x - px) <= Math.abs(vj.x - px) ? vi : vj;
        this.crashVehicleId = near.id;
        this.crashVehicleDx = near.x - px;
        return true;
      }
    }
    return false;
  }

  private onCrash(): void {
    const st = this.state;
    st.phase = "crashed";
    st.player.alive = false;
    this.crashTime = st.time;
    this.events.push({ type: "crash", vehicleId: this.crashVehicleId });
    this.spawnCrashCyclist();
  }

  /**
   * Spawn the thrown-rider dynamic capsule at the impact point and launch it
   * up/back/laterally with a tumble. It collides with STATIC only (the ground
   * half-space + guardrails), so it can never tunnel through the road and
   * never disturbs traffic. Rapier owns its motion from here; `syncCrashCyclist`
   * mirrors the pose into `state.crashCyclist` each crashed-phase step.
   */
  private spawnCrashCyclist(): void {
    if (!this.rapier || !this.physics) return;
    const rapier = this.rapier;
    const st = this.state;
    // Fly toward the side the crash vehicle came from (dead-centre → right).
    const side = this.crashVehicleDx >= 0 ? 1 : -1;

    const body = this.physics.createRigidBody(
      rapier.RigidBodyDesc.dynamic()
        .setTranslation(st.player.x, CYC_SPAWN_Y, 0)
        .setAngularDamping(CYC_ANG_DAMPING)
        .setLinearDamping(CYC_LIN_DAMPING),
    );
    // Rounded box, not a capsule — see the CYC_* block for why.
    this.physics.createCollider(
      new rapier.ColliderDesc(
        new rapier.RoundCuboid(CYC_HALF_X, CYC_HALF_Y, CYC_HALF_Z, CYC_BORDER),
      )
        .setCollisionGroups(COLLISION_CRASH_CYCLIST)
        .setFriction(CYC_FRICTION)
        .setRestitution(CYC_RESTITUTION),
      body,
    );
    body.setLinvel(
      { x: side * CYC_LAUNCH_LAT, y: CYC_LAUNCH_UP, z: CYC_LAUNCH_BACK },
      true,
    );
    // End-over-end tumble about X, with a little roll toward the launch side.
    body.setAngvel(
      { x: -CYC_TUMBLE, y: 0, z: side * CYC_TUMBLE * 0.35 },
      true,
    );
    this.crashCyclistBody = body;

    // Allocate the mirror object once (zero per-step allocation afterwards).
    st.crashCyclist = {
      x: st.player.x,
      y: CYC_SPAWN_Y,
      z: 0,
      quat: [0, 0, 0, 1],
      settled: false,
    };
  }

  /** Mirror the crash cyclist body pose into state (no allocation). */
  private syncCrashCyclist(): void {
    const body = this.crashCyclistBody;
    const c = this.state.crashCyclist;
    if (!body || !c) return;
    const t = body.translation();
    const r = body.rotation();
    // Grounded damping switch (see CYC_GROUNDED_Y): free tumble in the air,
    // heavy scrub once the body is down, so it stops instead of spinning.
    const grounded = t.y <= CYC_GROUNDED_Y;
    body.setAngularDamping(grounded ? CYC_GROUNDED_ANG_DAMPING : CYC_ANG_DAMPING);
    body.setLinearDamping(grounded ? CYC_GROUNDED_LIN_DAMPING : CYC_LIN_DAMPING);
    c.x = t.x;
    c.y = t.y;
    c.z = t.z;
    c.quat[0] = r.x;
    c.quat[1] = r.y;
    c.quat[2] = r.z;
    c.quat[3] = r.w;
    const lv = body.linvel();
    const av = body.angvel();
    const linSq = lv.x * lv.x + lv.y * lv.y + lv.z * lv.z;
    const angSq = av.x * av.x + av.y * av.y + av.z * av.z;
    c.settled =
      body.isSleeping() ||
      (linSq <= CYC_SETTLE_LIN * CYC_SETTLE_LIN &&
        angSq <= CYC_SETTLE_ANG * CYC_SETTLE_ANG);
  }

  /** Remove the crash cyclist body + clear its mirrored state. */
  private clearCrashCyclist(): void {
    if (this.crashCyclistBody && this.physics) {
      this.physics.removeRigidBody(this.crashCyclistBody);
    }
    this.crashCyclistBody = null;
    this.state.crashCyclist = null;
  }

  /**
   * Near-miss: a driving threat crosses z=0 this step with a lateral surface
   * gap in (0, nearMissMargin] and not yet awarded. Crossing is detected by
   * reconstructing the previous-step z from the live closing speed
   * (playerSpeed + absSpeed): prevZ < 0 && z >= 0.
   */
  private checkNearMiss(): void {
    const st = this.state;
    const px = st.player.x;
    const dt = T.sim.dt;
    const slots = this.pool.slots;
    for (let i = 0; i < this.pool.maxThreat; i++) {
      const v = slots[i];
      if (!isLive(v) || v.motion !== "driving" || v.role !== "threat") continue;
      if (v.nearMissAwarded) continue;
      const absSpeed = vehicleAbsSpeed(v);
      const prevZ = v.z - (st.player.forwardSpeed + absSpeed) * dt;
      if (prevZ < 0 && v.z >= 0) {
        // Same contact extent the crash test uses, so the near-miss band
        // (0, margin] starts exactly where a crash stops being possible.
        const gap = lateralGap(v, px);
        // gap > 0 ⇒ no contact (crash already checked); <= margin ⇒ near miss.
        if (gap > 0 && gap <= NEAR_MISS_MARGIN) {
          v.nearMissAwarded = true;
          this.scoring.recordNearMiss(st.time);
          this.events.push({ type: "nearMiss", vehicleId: v.id, margin: gap });
        }
      }
    }
  }

  /**
   * Arm every per-slot wreck clock for a freshly launched vehicle: the impact
   * detectors, the speed ratchet, the topple state (mode + spin-out direction,
   * both decided by `computeLaunch`) and the settle-refusal budgets.
   *
   * `kickedTipSign` is the corridor-away roll direction — the vehicle's own side
   * of the road — kept as the deterministic fallback for a side-roll topple that
   * cannot read a side off the slide (see `sideRollAxis`).
   */
  private armWreck(id: number, dx: number, launch: KickLaunch): void {
    this.kickedFalling[id] = 0;
    this.kickedGroundHit[id] = 0;
    this.kickedSpeedCap[id] = Infinity;
    this.resetToppleState(id);
    this.kickedTopple[id] = launch.topple === "sideRoll" ? 1 : 0;
    this.kickedYawSign[id] = launch.yawSign >= 0 ? 1 : -1;
    this.kickedTipSign[id] = dx >= 0 ? -1 : 1;
    this.kickedTipTime[id] = 0;
    this.kickedRefuseTime[id] = 0;
    this.kickedNoseTime[id] = 0;
    this.kickedNoseSpent[id] = 0;
    this.kickedNoseRefuse[id] = 0;
    this.kickedSlamArmed[id] = 0;
    this.kickedFrozen[id] = 0;
  }

  /** Clear the topple state for a slot (fresh launch, or a retired wreck). */
  private resetToppleState(i: number): void {
    this.kickedGroundTime[i] = 0;
    this.kickedTopple[i] = 0;
    this.kickedYawSign[i] = 1;
    this.kickedToppleTime[i] = 0;
    this.kickedToppleRot[i] = 0;
    this.kickedToppleDone[i] = 0;
  }

  /**
   * THE GROUND CONTROLLER — spin-out, then EXACTLY ONE topple.
   *
   * Owner playtest (2026-08-03, third rejection of the tumble model): "Still too
   * light and acrobatic. Make it SUPER HEAVY (just that we have a super strong
   * kick) and realistic how a car/big truck will crash in real physics."
   *
   * WHAT THIS REPLACES. `enforceWreckTumble` + `enforceWreckTip`: a friction-true
   * MULTI-FLIP tumble with a speed-coupled rate band, a rotation budget, a
   * spent-damping tail, a separate tip enforcement with its own axis selection
   * and yaw floor, and a nose-grind override layered over both. Three tuning
   * rounds could not make a model whose defining behaviour is "flips repeatedly"
   * read as heavy, because the defect was the MODEL. Deleted, not retuned.
   *
   * THE PHYSICS THIS IMPLEMENTS. A massive body taking a huge HORIZONTAL impulse
   * barely leaves the ground. The energy goes into a flat YAW SPIN-OUT while the
   * body slides backward on tyres and underside (this is what dashcam footage of
   * a real high-energy impact shows), and then — as lateral grip builds against
   * the direction of travel — the vehicle trips over its own wheels and topples
   * ONCE. Cars may go over their leading edge (`"trip"`) or onto a flank
   * (`"sideRoll"`); trucks and semis always roll sideways, slowly, because an
   * 11 m body with a high centre of mass cannot credibly go end-over-end.
   *
   * THE PHASES, all on one per-slot clock (`kickedGroundTime`, from touchdown):
   *
   *  0. CEILING + RATCHET. |ω| is clamped to `wreckMaxSpin` (contacts, not the
   *     controller, are what produce blur frames), and the horizontal speed is
   *     held under a monotone ratchet seeded at the landing speed, so no held
   *     rotation can ever act as a motor and buy the wreck extra travel. The
   *     ratchet releases for good below `groundReleaseSpeed`, because the topple
   *     MOVES the centre and a wound-down clamp would pin the wreck flat.
   *  1. SPIN-OUT (t < `toppleDelay`, and on until the topple completes). The yaw
   *     rate is floored at a speed-coupled `spinout*` rate in the direction the
   *     launch chose, so the flat slew survives the whole grind. Nothing touches
   *     the tilt: the wreck slides on its wheels, spinning.
   *  2. TOPPLE (t >= `toppleDelay`, once). One driven rotation about ONE axis at
   *     `toppleRate` (size-scaled, ramped in over `toppleRampTime`), until the
   *     pose is genuinely on its side or roof (|up·Y| < `toppleDoneDot` AND
   *     |long·Y| < `tipLongDot`). Hard-bounded by `toppleMaxRot` (447°) and
   *     `toppleMaxTime`, so "at most one topple" holds by construction.
   *  3. SLAM + RETIRE. Completion takes `toppleSlamKeep` of the horizontal speed
   *     and a downward `toppleSlamVy` kick — which makes the existing per-impact
   *     slam detector (syncKickedBodies, KICK_SLAM_ARM_VY) fire, so the landing
   *     gets its dust burst through the machinery that was already there — and
   *     raises the angular damping to `toppleSpentDamping` for the rest of the
   *     wreck's life. THAT is what makes "exactly one topple" true rather than
   *     likely: a wreck on its flank cannot roll on through onto its roof.
   *
   * The nose/tail guarantees survive intact: `syncKickedBodies` still runs the
   * continuous nose-stand clock, and a topple that strands the long axis past
   * `noseStandDot` for `noseGrindTime` switches its axis to `noseRightAxis`,
   * which provably lowers the long axis every step.
   *
   * Deterministic and rng-free — a pure function of the pose + velocity the
   * fixed-step integrator just produced (the only seeded input, the topple mode,
   * was drawn at launch). The stateHash replay is untouched.
   */
  private enforceWreckGround(): void {
    if (!this.physics) return;
    const dt = T.sim.dt;
    const slots = this.pool.slots;
    for (let i = 0; i < this.pool.maxThreat; i++) {
      const v = slots[i];
      if (!isLive(v) || v.motion !== "kicked" || !v.quat) continue;
      // SETTLED: the pose is final (velocities zeroed, body asleep). NOTE this
      // is the FREEZE flag, not `wreckFade > 0` — a blinking wreck is usually
      // still grinding and toppling, and the controller must keep driving it.
      if (this.kickedFrozen[i]) continue;
      if (!this.kickedGroundHit[i]) continue; // still on the (tiny) launch arc
      const handle = this.bodyHandles[i];
      if (handle < 0) continue;
      const rb = this.physics.getRigidBody(handle);
      const lv = rb.linvel();
      let speed = Math.hypot(lv.x, lv.z);

      // SIZE SCALE - every rate this controller holds or allows is scaled by
      // `min(1, tumbleRefLength / modelLength)` (a box truck 0.66x, a semi
      // 0.42x). It is not a look: rotational inertia grows with the SQUARE of
      // the length while the ground can only supply so much torque, and - the
      // failure this fixed - the TIP SPEED of a long body is |w| * L/2. Held at
      // the car's yaw, an 11 m semi swept its corners through the asphalt at
      // ~20 m/s, and the contact impulse that produced threw the whole body
      // 1.5 m into the air inside a fifth of a second (measured: centre
      // 2.03 m -> 3.47 m with the pose still 50 degrees from its side).
      // Scaled, a semi spins out at ~2.7 rad/s and stays welded to the road.
      const sizeScale = Math.min(1, K.tumbleRefLength / modelLength(v.model));

      // (0a) THE SPIN CEILING. The controller's own rates are bounded, but
      // CONTACTS are not: a body that catches a corner or clips a rail picks up
      // single-frame rates nothing asked for, and those frames are exactly the
      // acrobatic read. Rescaling the whole vector preserves the AXIS and sheds
      // only magnitude.
      const maxSpin = K.wreckMaxSpin * sizeScale;
      const cv = rb.angvel();
      const spin = Math.hypot(cv.x, cv.y, cv.z);
      if (spin > maxSpin) {
        const s = maxSpin / spin;
        rb.setAngvel({ x: cv.x * s, y: cv.y * s, z: cv.z * s }, true);
      }

      // (`kickedGroundTime` is advanced in `syncKickedBodies`, which runs
      // immediately before this pass and never skips a kicked slot.)

      // (0b) THE RATCHET — a wreck never travels faster than it landed.
      // Holding a spin on a body in ground contact is a MOTOR (Rapier turns the
      // enforced rotation back into travel, exactly like a driven wheel), so the
      // horizontal speed is capped by a monotone value seeded at touchdown and
      // only ever lowered. Released for good at a walking pace: the topple moves
      // the centre, and a wound-down clamp pins a nearly-stopped wreck flat.
      const cap = this.kickedSpeedCap[i];
      if (cap > K.groundReleaseSpeed) {
        if (speed > cap && speed > 1e-6) {
          const s = cap / speed;
          rb.setLinvel({ x: lv.x * s, y: lv.y, z: lv.z * s }, true);
          speed = cap;
        } else {
          this.kickedSpeedCap[i] = speed;
        }
      }

      // GROUNDED, with a STOPPED exemption. The height band is pose-aware
      // (`wreckGroundedY`), and a wreck that has stopped is by definition
      // standing on something solid however high its centre is — perched on a
      // guardrail, wedged on a prop — which is exactly the case the rest
      // guarantee has to cover.
      const stopped = Math.hypot(lv.x, lv.y, lv.z) < KICK_REST_LIN;
      const grounded = stopped || v.y <= this.wreckGroundedY(v);

      // RE-ARM — the guarantee's last mile. A topple that retired with the wreck
      // still lying flat (it completed at the `toppleDoneDot` lean and the body
      // then rocked back onto its wheels, or a hard contact stood it up again)
      // would be damped out flat and settle there, which is precisely the rest
      // the owner rejected. If a STOPPED wreck is still flat and the topple's own
      // budgets have not run out, the controller takes it back: `toppleDone` is
      // cleared and the damping restored, so the same bounded topple runs again.
      // Bounded by construction — the budgets are lifetime totals, not per-arm —
      // and it can never produce a second visible flip, because it only ever
      // fires on a wreck that failed to complete the first one.
      if (
        this.kickedToppleDone[i] &&
        stopped &&
        Math.abs(upDotY(v.quat)) > K.tipDot &&
        this.kickedToppleRot[i] < K.toppleMaxRot &&
        this.kickedToppleTime[i] < K.toppleMaxTime
      ) {
        this.kickedToppleDone[i] = 0;
        rb.setAngularDamping(K.wreckAngularDamping);
      }

      if (!this.kickedToppleDone[i]) {
        // (2) THE TOPPLE. Complete first: the pose may have got there on its own
        // (a hard slam, a rail), and the controller must never keep driving a
        // wreck that is already on its side.
        const upAbs = Math.abs(upDotY(v.quat));
        const longAbs = longDotY(v.quat);
        if (upAbs < K.toppleDoneDot && longAbs < K.tipLongDot) {
          this.finishTopple(i, rb);
        } else if (grounded && this.kickedGroundTime[i] >= K.toppleDelay) {
          // The topple rate is the tripping ceiling w ~ v / h with h grown to
          // match the body (see the size scale above): a semi goes over in about
          // a second, ponderously, which is the whole big-rig read.
          const ramp = Math.min(1, (this.kickedToppleTime[i] + dt) / K.toppleRampTime);
          let rate = K.toppleRate * sizeScale * ramp;
          // NOSE-GRIND OVERRIDE. A `"trip"` topple swings the long axis through
          // vertical; if it STICKS there (0.3 s of continuous grounded
          // nose-standing) the axis switches to the one that provably lowers the
          // long axis every step, at the forced rate. Time-boxed on its own
          // budget so the exemption is bounded.
          const nosed =
            longDotY(v.quat) > K.noseStandDot &&
            this.kickedNoseTime[i] >= K.noseGrindTime &&
            this.kickedNoseSpent[i] < K.noseMaxTime;
          let axis: { x: number; z: number };
          if (nosed) {
            axis = noseRightAxis(v.quat, lv.x, lv.z);
            rate = Math.max(rate, K.noseForceRate * Math.sqrt(sizeScale));
            this.kickedNoseSpent[i] += dt;
          } else if (this.kickedTopple[i] === 1) {
            axis = sideRollAxis(
              v.quat, lv.x, lv.z, rb.angvel(), this.kickedTipSign[i],
            );
          } else {
            axis = frictionTumbleAxis(lv.x, lv.z);
          }
          // BOOST, NEVER BRAKE, and only along the axis: the rotation already
          // present about the topple axis is topped up to the target, and
          // everything orthogonal (the spin-out yaw, whatever the grind gave it)
          // is left exactly as the integrator produced it.
          const av = rb.angvel();
          const along = av.x * axis.x + av.z * axis.z;
          if (along < rate) {
            const boost = rate - along;
            rb.setAngvel(
              { x: av.x + axis.x * boost, y: av.y, z: av.z + axis.z * boost },
              true,
            );
          }
          this.kickedToppleTime[i] += dt;
          this.kickedToppleRot[i] += rate * dt;
          // HARD BOUNDS. Either exhausts the topple for good — the wreck is left
          // to unforced physics with its spin damped out, and the settle
          // refusals in `checkKickedLanded` (which have their own budgets) are
          // what keep the pose honest from there.
          if (
            this.kickedToppleRot[i] >= K.toppleMaxRot ||
            this.kickedToppleTime[i] >= K.toppleMaxTime
          ) {
            this.finishTopple(i, rb);
          }
        }
      }

      // (1) SPIN-OUT YAW. Held only while the wreck is still on its wheels and
      // genuinely sliding: the flat slew is the grind, and it must die with it.
      // A toppled wreck is never spun — a car lying on its flank pirouetting on
      // the asphalt is the toy read this whole pass exists to remove.
      if (
        !this.kickedToppleDone[i] &&
        grounded &&
        speed >= K.spinoutMinSpeed &&
        !stopped
      ) {
        const target =
          Math.min(
            K.spinoutYawMax,
            Math.max(K.spinoutYawMin, speed * K.spinoutYawPerSpeed),
          ) * sizeScale;
        const av = rb.angvel();
        const want = this.kickedYawSign[i] * target;
        if (Math.abs(av.y) < target) {
          rb.setAngvel({ x: av.x, y: want, z: av.z }, true);
        }
      }
    }
  }

  /**
   * End a wreck's topple: SLAM (a hard horizontal haircut plus a downward kick,
   * which fires the existing per-impact slam detector and with it the dust
   * burst) and retire the controller for that wreck's whole life by raising its
   * angular damping to `toppleSpentDamping`.
   *
   * Called both on a genuine completion (the pose reached side/roof) and on the
   * hard bounds (`toppleMaxRot` / `toppleMaxTime`); the damping is what makes
   * "exactly one topple" a property of the model rather than of the tuning.
   */
  private finishTopple(i: number, rb: RAPIER.RigidBody): void {
    this.kickedToppleDone[i] = 1;
    const lv = rb.linvel();
    rb.setLinvel(
      {
        x: lv.x * K.toppleSlamKeep,
        y: Math.min(lv.y, K.toppleSlamVy),
        z: lv.z * K.toppleSlamKeep,
      },
      true,
    );
    rb.setAngularDamping(K.toppleSpentDamping);
  }

  /**
   * Height (m) of the body centre below which a kicked wreck counts as ON THE
   * GROUND. Uses the LARGEST half-extent of the model box (a wreck resting on
   * its side sits at half its width, on its wheels at half its height), plus
   * the GUARDRAIL height and a little slack for the contact skin.
   *
   * The guardrail term is not padding: the wrecks that used to escape the
   * enforcement were the ones that ended up PERCHED on a rail — measured at
   * y ≈ 1.3-1.5 m for a sedan whose own resting height is 0.73 m, tilted only
   * ~16°, i.e. exactly the flat rest the owner rejected. A rail-perched wreck
   * is standing on something solid and is every bit as tippable as one on the
   * asphalt, so the band has to reach it.
   */
  private wreckGroundedY(v: VehicleEntity): number {
    const h = Math.max(modelHeight(v.model), modelWidth(v.model)) / 2;
    const flat = h + R.guardrailHeight + 0.35;
    if (!v.quat) return flat;
    // POSE-AWARE HALF-EXTENT — the fix for the nose/tail hole (owner playtest
    // 2026-08-03). The constant above is the half-extent of a wreck lying FLAT,
    // and it structurally excludes the very pose the guarantee has to cover: a
    // car standing on its nose is supported by its LENGTH, so its centre sits
    // ~2.3 m up — above the band — and BOTH enforcements skipped it. The wreck
    // was standing on solid ground and the sim considered it airborne.
    //
    // The exact half-extent of the oriented box projected onto world Y is
    // 0.5·(|L·Y|·length + |U·Y|·height + |R·Y|·width); it degenerates to
    // height/2 for a flat car and to length/2 for a nose stand, which is exactly
    // the band each pose needs. Taken as a MAX with the flat constant so this
    // can only ever widen the band — the rail-perched cases the constant was
    // sized for are untouched.
    const [qx, qy, qz, qw] = v.quat;
    const ly = 2 * (qy * qz - qw * qx); // long axis (local +Z) · Y
    const uy = 1 - 2 * (qx * qx + qz * qz); // up axis (local +Y) · Y
    const ry = 2 * (qx * qy + qw * qz); // right axis (local +X) · Y
    const proj =
      0.5 *
      (Math.abs(ly) * modelLength(v.model) +
        Math.abs(uy) * modelHeight(v.model) +
        Math.abs(ry) * modelWidth(v.model));
    return Math.max(flat, proj + R.guardrailHeight + 0.35);
  }

  /** Kicked vehicle at rest (any height) or out of bounds → down + despawn. */
  private checkKickedLanded(): void {
    const slots = this.pool.slots;
    for (let i = 0; i < this.pool.maxThreat; i++) {
      const v = slots[i];
      if (!isLive(v) || v.motion !== "kicked") {
        this.kickedRestSteps[i] = 0;
        this.kickedFalling[i] = 0;
        this.kickedGroundHit[i] = 0;
        this.kickedSpeedCap[i] = Infinity;
        this.resetToppleState(i);
        this.kickedTipTime[i] = 0;
        this.kickedRefuseTime[i] = 0;
        this.kickedNoseTime[i] = 0;
        this.kickedNoseSpent[i] = 0;
        this.kickedNoseRefuse[i] = 0;
        this.kickedSlamArmed[i] = 0;
        this.kickedFrozen[i] = 0;
        continue;
      }
      // OUT OF THE WORLD — retires immediately, blink or no blink. There is
      // nothing on screen to flicker.
      if (v.z > KICK_OOB_Z || Math.abs(v.x) > KICK_OOB_X || v.z < -KICK_OOB_Z) {
        this.retireWreck(i, v);
        continue;
      }
      // BLINK-OUT — a DESPAWN CLOCK armed off TOUCHDOWN, not off the settle.
      //
      // Owner note (2026-08-03): a wreck that flashes only once it has finished
      // crashing blocks the corridor for the whole slide. `wreckFadeDelay`
      // (0.35 s) after the first ground impact the countdown starts, and when it
      // expires `kickedVehicleDown` fires and the slot goes back to the pool —
      // UNCONDITIONALLY, whether or not the wreck ever settled. A wreck still in
      // motion at expiry (rare: slow semis) vanishes mid-slide, which is honest
      // arcade language because it has been flashing the whole way down.
      //
      // The countdown is a pure sim value quantized to the fixed step, so the
      // render's blink phase is identical on every replay of the same seed.
      if (v.wreckFade > 0) {
        v.wreckFade -= T.sim.dt;
        if (v.wreckFade <= 1e-9) {
          v.wreckFade = 0;
          this.retireWreck(i, v);
          continue;
        }
      } else if (
        this.kickedGroundHit[i] &&
        this.kickedGroundTime[i] >= K.wreckFadeDelay - 1e-9
      ) {
        v.wreckFade = K.wreckFadeTime;
      }
      // A settled wreck is done: pose frozen, nothing left to test. It keeps
      // flashing out the remainder of its countdown above.
      if (this.kickedFrozen[i]) continue;
      // Rest test from the BODY's motion (see KICK_REST_* above): sleeping, or
      // both velocities under threshold for KICK_REST_STEPS consecutive steps.
      let atRest = false;
      const handle = this.bodyHandles[i];
      if (this.physics && handle >= 0) {
        const rb = this.physics.getRigidBody(handle);
        if (rb.isSleeping()) {
          atRest = true;
        } else {
          const lv = rb.linvel();
          const av = rb.angvel();
          const linSq = lv.x * lv.x + lv.y * lv.y + lv.z * lv.z;
          const angSq = av.x * av.x + av.y * av.y + av.z * av.z;
          atRest =
            linSq <= KICK_REST_LIN * KICK_REST_LIN &&
            angSq <= KICK_REST_ANG * KICK_REST_ANG;
        }
      }
      // UPRIGHT WRECKS MAY NOT SETTLE. The tip enforcement is still working on
      // this body (`enforceWreckGround`), and letting the rest test win first is
      // exactly how wrecks used to freeze wheels-down: the roll it is being
      // given is below KICK_REST_ANG, so a nearly-tipped wreck reads as "at
      // rest" for the whole tip.
      //
      // The refusal has its OWN budget (`kickedRefuseTime`, capped at the same
      // tipMaxTime): a wreck the enforcement never gets to act on — perched
      // above the grounded height, wedged on a rail — must still settle and
      // return its pool slot eventually. Keeping it separate from
      // `kickedTipTime` matters: billing both clocks out of one budget halved
      // the enforcement window and left ~3% of wrecks resting flat.
      if (
        atRest &&
        v.quat !== null &&
        Math.abs(upDotY(v.quat)) > K.tipDot &&
        this.kickedRefuseTime[i] < K.tipMaxTime
      ) {
        atRest = false;
        this.kickedRefuseTime[i] += T.sim.dt;
      }
      // ...AND NOSE/TAIL-STANDING WRECKS MAY NOT SETTLE EITHER.
      //
      // The refusal above only ever looked at the body's UP axis, which is blind
      // to a wreck balanced on its front bumper (up horizontal, long axis
      // vertical — see `longDotY`). A legitimate rest for a car-shaped box is
      // SIDE or ROOF, and both put the long axis near-horizontal, so the rest
      // test now demands BOTH: |up · Y| < tipDot AND |long · Y| < tipLongDot.
      // Own budget (`kickedNoseRefuse`), same expiry discipline as above, so a
      // pathological pose can never leak the pool slot.
      if (
        atRest &&
        v.quat !== null &&
        longDotY(v.quat) > K.tipLongDot &&
        this.kickedNoseRefuse[i] < K.noseMaxTime
      ) {
        atRest = false;
        this.kickedNoseRefuse[i] += T.sim.dt;
      }
      this.kickedRestSteps[i] = atRest ? this.kickedRestSteps[i] + 1 : 0;
      if (this.kickedRestSteps[i] >= KICK_REST_STEPS) {
        // SETTLED. The wreck is done crashing; it stays live (flashing) until
        // its countdown above expires, and never moves again.
        this.kickedRestSteps[i] = 0;
        this.kickedFrozen[i] = 1;
        // SAFETY NET — a wreck that came to rest without ever latching a
        // touchdown (`kickedGroundHit`) has no blink clock, and the blink clock
        // is now the ONLY thing that despawns it. It happens: a body dropped
        // exactly onto its resting height with no vertical speed (a test rig, a
        // wreck that slid to a halt on a prop) never arms the fall detector, and
        // Rapier sleeps it before it can. Arm the countdown here so the settle
        // path can never leak a pool slot.
        if (v.wreckFade <= 0) v.wreckFade = K.wreckFadeTime;
        // FREEZE THE FINAL POSE. Both enforcements and the rest test bail out on
        // `kickedFrozen`, which was only true by assumption before: a wreck that
        // settled resting on its side with a hair of residual motion could
        // topple back onto its wheels during the blink, unwatched, and the
        // no-flat-rest guarantee was silently broken at the last moment
        // (measured: 1 of 352 wrecks, seed 2446971, ending at up · worldUp =
        // 0.642 after passing the tilt test 60 steps earlier). Zeroing the
        // velocities makes the claim literally true, and it is what the player
        // sees anyway — the wreck is flickering out, not still crashing.
        const fh = this.bodyHandles[i];
        if (this.physics && fh >= 0) {
          const frb = this.physics.getRigidBody(fh);
          frb.setLinvel({ x: 0, y: 0, z: 0 }, false);
          frb.setAngvel({ x: 0, y: 0, z: 0 }, false);
          frb.sleep();
        }
      }
    }
  }

  /**
   * End a wreck's life: emit `kickedVehicleDown`, park its Rapier body back as
   * a disabled kinematic, and return the slot to the pool. Shared by the
   * out-of-bounds path (immediate) and the blink-out path (when the countdown
   * armed at touchdown + TUNING.kick.wreckFadeDelay expires).
   *
   * The body may still be MOVING here — the blink expires on a clock, not on
   * the settle, so a wreck that is still grinding when its countdown runs out is
   * retired mid-motion. Its velocities are therefore zeroed before it is parked:
   * `setBodyType(KinematicPositionBased)` on a live dynamic body leaves the
   * stale linvel/angvel on the handle, and the next launch through this slot
   * would inherit them for the frame between `setBodyType(Dynamic)` and
   * `applyKick`'s own `setLinvel`.
   */
  private retireWreck(i: number, v: VehicleEntity): void {
    this.kickedRestSteps[i] = 0;
    this.kickedFalling[i] = 0;
    this.kickedGroundHit[i] = 0;
    this.kickedSpeedCap[i] = Infinity;
    this.resetToppleState(i);
    this.kickedTipTime[i] = 0;
    this.kickedRefuseTime[i] = 0;
    this.kickedNoseTime[i] = 0;
    this.kickedNoseSpent[i] = 0;
    this.kickedNoseRefuse[i] = 0;
    this.kickedSlamArmed[i] = 0;
    this.kickedFrozen[i] = 0;
    this.events.push({
      type: "kickedVehicleDown",
      vehicleId: v.id,
      x: v.x,
      z: v.z,
    });
    // Disable + reset the body to kinematic, then despawn the slot.
    const body = this.bodies[i];
    if (body) {
      body.setLinvel({ x: 0, y: 0, z: 0 }, false);
      body.setAngvel({ x: 0, y: 0, z: 0 }, false);
      body.setEnabled(false);
      body.setBodyType(2 /* KinematicPositionBased */, false);
    }
    v.quat = null;
    despawnVehicle(this.pool, v, this.events);
  }

  // -------------------------------------------------------------------------
  // Phase control
  // -------------------------------------------------------------------------

  private startRun(): void {
    const st = this.state;
    st.phase = "playing";
    st.time = 0;
    st.player.alive = true;
    if (this.playerBody) this.playerBody.setEnabled(true);
  }

  reset(seed?: number): void {
    const newSeed = seed ?? this.state.seed;
    // Despawn everything AND rewind the pool's round-robin slot cursors — the
    // slot index picks the vehicle MODEL (hence its collider + crash extents),
    // so a pool left mid-cycle would hand a reset run different hitboxes than a
    // fresh world with the same seed.
    resetVehiclePool(this.pool, this.events);
    this.events.length = 0;

    // Reset subsystems. The director is stateless (a pure function of time);
    // everything else carries run state that MUST be rewound here:
    //  - the threat spawner (RNG stream + clock + path timeline + lane cursors)
    //    is rebuilt from the seed,
    //  - the ambient spawner's distance cursors are rewound (they are keyed on
    //    player.distance, which restarts at 0),
    //  - the kick / ambient RNG streams are re-seeded.
    this.spawner = createSpawner(newSeed);
    this.ambient.reset();
    this.coins.reset(newSeed);
    this.kickedRestSteps.fill(0);
    this.kickedFalling.fill(0);
    this.kickedGroundHit.fill(0);
    this.kickedTipSign.fill(1);
    this.kickedTipTime.fill(0);
    this.kickedRefuseTime.fill(0);
    this.kickedNoseTime.fill(0);
    this.kickedNoseSpent.fill(0);
    this.kickedNoseRefuse.fill(0);
    this.kickedSlamArmed.fill(0);
    this.kickedFrozen.fill(0);
    this.scoring.reset();
    this.kickRng = mulberry32(newSeed ^ 0x5eed);
    this.ambientRng = mulberry32(newSeed ^ 0xa7b);

    // Reset player.
    const p = createPlayerState();
    this.state.player = p;
    this.state.score = this.scoring.state;
    this.state.vehicles = this.pool.slots;
    this.state.coins = this.coins.coins;

    // Reset Rapier bodies: disable all threat bodies, reset player.
    for (let i = 0; i < this.bodies.length; i++) {
      const b = this.bodies[i];
      if (b) {
        b.setEnabled(false);
        b.setBodyType(2 /* KinematicPositionBased */, false);
      }
    }
    if (this.playerBody) this.playerBody.setEnabled(false);

    // Remove the thrown-rider body + clear its mirrored pose.
    this.clearCrashCyclist();

    this.hitstopRemaining = 0;
    this.crashTime = null;
    this.crashVehicleId = -1;
    this.crashVehicleDx = 0;
    this.state.phase = "title";
    this.state.time = 0;
    this.state.seed = newSeed;
  }

  /**
   * The coin layer (sim/coins.ts). Exposed for tests + QA instrumentation only:
   * gameplay reads coins through `state.coins` and the `coin` event.
   */
  get coinField(): CoinField {
    return this.coins;
  }

  pause(): void {
    if (this.state.phase === "playing") this.state.phase = "paused";
  }

  resume(): void {
    if (this.state.phase === "paused") this.state.phase = "playing";
  }

  /** Leave the title phase and start the run. Idempotent: no-op unless the
   *  current phase is "title". Used by click-to-start (HUD canvas click). */
  start(): void {
    if (this.state.phase === "title") this.startRun();
  }

  // -------------------------------------------------------------------------
  // stateHash — FNV-1a over quantized gameplay state.
  // -------------------------------------------------------------------------

  stateHash(): string {
    const st = this.state;
    const p = st.player;
    // Quantize floats to 3 decimal places (mm) for stable hashing.
    const q = (n: number): string => (Math.round(n * 1000) / 1000).toString();
    let s = `${st.phase}|${q(st.time)}|${q(p.x)}|${q(p.vx)}|${q(p.forwardSpeed)}|${q(p.distance)}|${p.alive ? 1 : 0}|${q(p.kickCooldownRemaining)}`;
    s += `|${q(st.score.distanceScore)}|${q(st.score.bonusScore)}|${q(st.score.multiplier)}|${q(st.score.total)}|${st.score.kicks}|${st.score.nearMisses}|${st.score.coins}`;
    // Active vehicles: id/kind/motion/quantized pos+yaw.
    const slots = this.pool.slots;
    for (let i = 0; i < slots.length; i++) {
      const v = slots[i];
      if (!isLive(v)) continue;
      s += `|${v.id}|${v.kind}|${v.role}|${v.motion}|${q(v.x)}|${q(v.y)}|${q(v.z)}|${q(v.yaw)}`;
      // The blink-out countdown is gameplay state (it gates the despawn) AND
      // the render's blink phase, so it belongs in the determinism hash — as
      // does the settle freeze, which gates every wreck enforcement.
      if (v.wreckFade > 0) s += `|f${q(v.wreckFade)}`;
      if (this.kickedFrozen[i]) s += "|z";
      if (v.quat) s += `|${q(v.quat[0])}|${q(v.quat[1])}|${q(v.quat[2])}|${q(v.quat[3])}`;
    }
    // Live coins. Their placement is a seeded draw off the spawn stream and
    // their collection is a gameplay state change, so both belong in the hash.
    const coins = st.coins;
    for (let i = 0; i < coins.length; i++) {
      const c = coins[i];
      if (!c.active) continue;
      s += `|k${c.id}|${q(c.x)}|${q(c.z)}`;
    }
    // Crash cyclist (deterministic: Rapier fixed-step integration of a body
    // spawned from the deterministic crash pose).
    const cc = st.crashCyclist;
    if (cc) {
      s += `|cyc|${q(cc.x)}|${q(cc.y)}|${q(cc.z)}|${q(cc.quat[0])}|${q(cc.quat[1])}|${q(cc.quat[2])}|${q(cc.quat[3])}|${cc.settled ? 1 : 0}`;
    }
    // FNV-1a over the string.
    let h = 0x811c9dc5;
    for (let i = 0; i < s.length; i++) {
      h ^= s.charCodeAt(i);
      h = Math.imul(h, 0x01000193);
    }
    return (h >>> 0).toString(16).padStart(8, "0");
  }

  // -------------------------------------------------------------------------
  // Teardown
  // -------------------------------------------------------------------------

  dispose(): void {
    if (this.eventQueue) this.eventQueue.free();
    if (this.physics) this.physics.free();
  }
}

// ---------------------------------------------------------------------------
// Construction — async (waits for Rapier) or sync (caller provides RAPIER).
// ---------------------------------------------------------------------------

/**
 * Create a sim world, awaiting Rapier WASM init. The world starts in the
 * `title` phase; the first `step` with any input begins a run.
 */
export async function createWorld(seed: number): Promise<World> {
  const rapier = await initPhysics();
  const w = new World(seed);
  w.init(rapier);
  return w;
}

/**
 * Create a sim world synchronously using an already-initialized RAPIER
 * namespace (for headless tests that share one Rapier init).
 */
export function createWorldSync(seed: number, rapier: typeof RAPIER): World {
  const w = new World(seed);
  w.init(rapier);
  return w;
}
